import {cx} from './cx';

/**
 * A dish photo — **or nothing at all**.
 *
 * # The no-photo state is the design, not a fallback
 *
 * `image_url` is null for most V1 merchants and photo sourcing is unresolved, so
 * every layout on this surface is drawn as type and price, complete without a
 * picture. A photo *joins* an already-finished row. `test/guest-path.test.tsx`
 * asserts that a menu whose items have no `image_url` renders **zero** `img` or
 * `picture` elements anywhere on the path a guest walks — so this component
 * returns `null` for a missing, empty or whitespace `src`, and there is no grey
 * box, no "no photo" caption and no reserved space.
 *
 * That is the rule the design package gets wrong: it draws a striped placeholder
 * box, and a menu of ninety striped boxes reads as a page that failed to load.
 * The stripe survives only as the **background behind a real image** — visible
 * while it decodes, and in the gap where a slow photo has not arrived — which is
 * `--pokpok-photo-placeholder` in the stylesheet.
 *
 * # `alt` is empty on purpose
 *
 * The dish name is already beside the photo in text. A second announcement of
 * the same name is noise, and a generated description would be a claim about
 * food nobody verified. Pass `alt` only for a photo that carries information the
 * text does not — which, on this surface, is none of them.
 */
export function Photo({
  src,
  shape,
  alt = '',
  className,
}: {
  /** `ChannelMenuItem.image_url`, straight through. Null renders nothing. */
  src: string | null | undefined;
  /** `thumb` is the 56px list row; `hero` is the item screen's banner. */
  shape: 'thumb' | 'hero';
  alt?: string;
  className?: string;
}) {
  if (!src || src.trim() === '') return null;

  // Explicit width and height: a photo that arrives late must not shove the row
  // it belongs to down the page under a scrolling thumb.
  const box = shape === 'thumb' ? {width: 56, height: 56} : {width: 720, height: 360};

  return (
    <img
      className={cx('pk-photo', `pk-photo--${shape}`, className)}
      src={src}
      alt={alt}
      width={box.width}
      height={box.height}
      loading="lazy"
      decoding="async"
    />
  );
}
